/**
 * The Capacitor seam.
 *
 * Everything upstream of this file talks to `LocationProvider`, never to
 * `navigator.geolocation` directly. Going native means adding one more
 * implementation here (Capacitor's background-geolocation plugin) and changing
 * which one `getLocationProvider()` returns — nothing else moves.
 *
 * Be clear about what the browser can actually do: `watchPosition` runs while
 * the page is visible and is throttled or suspended the moment it isn't. iOS
 * stops it outright. There is no web API for background geofencing — the W3C
 * Geofencing spec was abandoned and never shipped anywhere. So a PWA nudges you
 * while the app is open, and that's the honest ceiling.
 */

export interface Fix {
  lat: number
  lon: number
  accuracyM: number | null
  at: number
}

export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported'

export interface LocationProvider {
  readonly canRunInBackground: boolean
  isSupported(): boolean
  getPermission(): Promise<PermissionState>
  getCurrent(): Promise<Fix>
  watch(onFix: (fix: Fix) => void, onError: (err: Error) => void): () => void
}

function toFix(p: GeolocationPosition): Fix {
  return {
    lat: p.coords.latitude,
    lon: p.coords.longitude,
    accuracyM: Number.isFinite(p.coords.accuracy) ? p.coords.accuracy : null,
    at: p.timestamp,
  }
}

/** Turns the numeric GeolocationPositionError into something actionable. */
function describe(err: GeolocationPositionError): Error {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return new Error(
        'Location permission denied. Nudge cannot trigger reminders without it — ' +
          'enable location for this site in your browser settings.',
      )
    case err.POSITION_UNAVAILABLE:
      return new Error(
        'No position available. This usually means no GPS or network fix — ' +
          'try moving somewhere with a clearer signal.',
      )
    case err.TIMEOUT:
      return new Error('Timed out waiting for a position fix.')
    default:
      return new Error(`Geolocation failed: ${err.message || 'unknown error'}`)
  }
}

export class BrowserLocationProvider implements LocationProvider {
  /** Always false on the web. Stated explicitly so callers can warn the user. */
  readonly canRunInBackground = false

  isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'geolocation' in navigator
  }

  async getPermission(): Promise<PermissionState> {
    if (!this.isSupported()) return 'unsupported'
    if (!navigator.permissions?.query) return 'prompt'
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' })
      return status.state as PermissionState
    } catch {
      // Firefox historically threw here; treat as "we'll find out by asking".
      return 'prompt'
    }
  }

  getCurrent(): Promise<Fix> {
    if (!this.isSupported()) {
      return Promise.reject(new Error('This browser has no Geolocation API.'))
    }
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (p) => resolve(toFix(p)),
        (e) => reject(describe(e)),
        { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
      )
    })
  }

  watch(onFix: (fix: Fix) => void, onError: (err: Error) => void): () => void {
    if (!this.isSupported()) {
      onError(new Error('This browser has no Geolocation API.'))
      return () => {}
    }
    const id = navigator.geolocation.watchPosition(
      (p) => onFix(toFix(p)),
      (e) => onError(describe(e)),
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 10_000 },
    )
    return () => navigator.geolocation.clearWatch(id)
  }
}

let provider: LocationProvider = new BrowserLocationProvider()

export function getLocationProvider(): LocationProvider {
  return provider
}

/** Swap in the native provider (Capacitor) or a fake for tests. */
export function setLocationProvider(next: LocationProvider): void {
  provider = next
}

const EARTH_R = 6_371_000

/** Great-circle distance in metres. Used only for display; the server decides. */
export function haversine(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(bLat - aLat)
  const dLon = toRad(bLon - aLon)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(s)))
}

export const M_PER_MILE = 1609.344

export function formatDistance(metres: number | null | undefined): string {
  if (metres == null || !Number.isFinite(metres)) return ''
  const miles = metres / M_PER_MILE
  if (miles < 0.1) return 'right here'
  return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi away`
}
